import React, { useState } from 'react';
import _ from 'lodash';
import {
  Box, Typography, Chip, IconButton, Tooltip, Collapse, Button, Snackbar, Alert,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { statusChip } from '../../utils/constants/financeStatus.constant';
import CompletePaymentModal from '../Modal/CompletePaymentModal';
import ProjectRow from './ProjectRow';

import { getBankLabel } from '../../constants/thaiBanks';
// ─── Level 2: Payment table (header + rows) ──────────────────────────────────
export function PaymentTable({ paymentsOnDate }) {
  return (
    <Box sx={{ borderLeft: '3px solid #6366f1' }}>
      {paymentsOnDate.length === 0 ? (
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">ไม่มีรายการ Payment</Typography>
        </Box>
      ) : (
        paymentsOnDate.map((payment, idx) => (
          <PaymentRow key={payment._id} payment={payment} seq={idx + 1} />
        ))
      )}
    </Box>
  );
}

// ─── Level 2: Payment row ────────────────────────────────────────────────────
export default function PaymentRow({ payment, seq }) {
  const [open, setOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [copySnack, setCopySnack] = useState({ open: false, text: '' });
  const expCount = payment.expenses_list?.length || 0;

  const projectGroups = _.groupBy(
    payment.expenses_list || [],
    (e) => e.project?.id || e.project?._id || '__none__',
  );
  const projectKeys = Object.keys(projectGroups);

  const handleCopy = (value, label) => {
    if (!value) return;
    navigator.clipboard.writeText(String(value)).then(() => {
      setCopySnack({ open: true, text: `คัดลอก ${label}: ${value}` });
    });
  };

  return (
    <Box sx={{ borderBottom: '1px solid rgb(var(--color-primary-100))' }}>
      {/* Payment card header */}
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.2,
          cursor: 'pointer',
          backgroundColor: open ? 'rgb(var(--color-primary-50))' : '#fff',
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: open ? 'rgb(var(--color-primary-50))' : '#f5f3ff' },
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <IconButton size="small" sx={{ mr: 0.5, flexShrink: 0, color: open ? 'rgb(var(--color-primary-450))' : 'rgb(var(--color-primary-300))' }} disableRipple>
          {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>

        {/* Seq */}
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 20, textAlign: 'center', mr: 1, flexShrink: 0 }}>
          {seq}
        </Typography>

        {/* Doc number */}
        <Box sx={{ mr: 1.5, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2} sx={{ fontSize: '0.65rem' }}>เลขที่เอกสาร</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="PMT" size="small" sx={{ height: 15, fontSize: '0.5rem', fontWeight: 700, backgroundColor: 'rgb(var(--color-primary-100))', color: 'rgb(var(--color-primary-600))', borderRadius: 0.5, '& .MuiChip-label': { px: 0.5 } }} />
            <Typography variant="body2" fontWeight={700} color="rgb(var(--color-primary-600))" noWrap>
              {payment.payment_number}
            </Typography>
          </Box>
        </Box>

        {/* Payee — shrinkable */}
        <Tooltip title={payment.payee?.name || '-'} arrow placement="top">
          <Box sx={{ mr: 1.5, flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2} sx={{ fontSize: '0.65rem' }}>ผู้รับเงิน</Typography>
            <Typography variant="body2" fontWeight={500} noWrap sx={{ textOverflow: 'ellipsis' }}>
              {payment.payee?.name || '-'}
            </Typography>
          </Box>
        </Tooltip>

        {/* เลขบัญชี (single-click to copy) */}
        <Tooltip title="คลิกเพื่อคัดลอกเลขบัญชี" arrow>
          <Box
            sx={{ mr: 1, flexShrink: 0, cursor: 'pointer', borderRadius: 1, px: 0.5, '&:hover': { backgroundColor: '#ede9fe' }, transition: 'background-color 0.15s' }}
            onClick={(e) => { e.stopPropagation(); handleCopy(payment.payee?.account_number, 'เลขบัญชี'); }}
          >
            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2} sx={{ fontSize: '0.65rem' }}>เลขบัญชี</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Typography variant="body2" fontWeight={500} noWrap fontSize="0.78rem" color="rgb(var(--color-primary-600))">{payment.payee?.account_number || '-'}</Typography>
              <ContentCopyIcon sx={{ fontSize: 10, color: 'rgb(var(--color-primary-300))', flexShrink: 0 }} />
            </Box>
          </Box>
        </Tooltip>

        {/* ธนาคาร */}
        <Tooltip title={getBankLabel(payment.payee?.bank) || '-'} arrow placement="top">
          <Box sx={{ mr: 1.5, flexShrink: 1, minWidth: 0, overflow: 'hidden', maxWidth: 120 }}>
            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2} sx={{ fontSize: '0.65rem' }}>ธนาคาร</Typography>
            <Typography variant="body2" color="text.secondary" noWrap fontSize="0.75rem" sx={{ textOverflow: 'ellipsis' }}>
              {getBankLabel(payment.payee?.bank) || '-'}
            </Typography>
          </Box>
        </Tooltip>

        {/* Count */}
        <Box sx={{ textAlign: 'center', mr: 1, flexShrink: 0, minWidth: 36 }}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2} sx={{ fontSize: '0.65rem' }}>รายการ</Typography>
          <Typography variant="body2" fontWeight={600}>{expCount}</Typography>
        </Box>

        {/* ยอดรวมทั้งหมด / จ่ายแล้ว / ยอดคงค้าง */}
        {(() => {
          const total = payment.totalAmount || 0;
          const paid = payment.status === 'SUCCESS'
            ? total
            : (payment.paid_amount || 0);
          const remaining = Math.max(total - paid, 0);
          return (
            <>
              <Box sx={{ textAlign: 'right', mr: 0.5, flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2} sx={{ fontSize: '0.6rem' }}>ยอดรวมทั้งหมด</Typography>
                <Typography variant="body2" fontWeight={700} color="#1e293b" noWrap fontSize="0.8rem">
                  ฿{total.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right', mr: 0.5, flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2} sx={{ fontSize: '0.6rem' }}>จ่ายแล้ว</Typography>
                <Typography variant="body2" fontWeight={700} color="#16a34a" noWrap fontSize="0.8rem">
                  ฿{paid.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Tooltip title="คลิกเพื่อคัดลอกยอดคงค้าง" arrow>
                <Box
                  sx={{ textAlign: 'right', mr: 1, flexShrink: 0, cursor: 'pointer', borderRadius: 1, px: 0.5, '&:hover': { backgroundColor: '#fef2f2' }, transition: 'background-color 0.15s' }}
                  onClick={(e) => { e.stopPropagation(); handleCopy(remaining.toFixed(2), 'ยอดคงค้าง'); }}
                >
                  <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2} sx={{ fontSize: '0.6rem' }}>ยอดคงค้าง</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.2 }}>
                    <Typography variant="body2" fontWeight={700} color={remaining > 0 ? '#dc2626' : '#16a34a'} noWrap fontSize="0.8rem">
                      ฿{remaining.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                    </Typography>
                    {remaining > 0 && <ContentCopyIcon sx={{ fontSize: 10, color: '#fca5a5', flexShrink: 0 }} />}
                  </Box>
                </Box>
              </Tooltip>
            </>
          );
        })()}

        {/* Status */}
        <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center', mr: 1 }}>
          {statusChip(payment.status)}
        </Box>

        {/* จ่ายเงิน button */}
        {payment.status !== 'SUCCESS' && (
          <Button
            size="small"
            variant="contained"
            onClick={(e) => { e.stopPropagation(); setCompleteOpen(true); }}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap', backgroundColor: '#15803d', '&:hover': { backgroundColor: '#166534' }, fontSize: '0.7rem', px: 1.2, py: 0.4, minWidth: 'auto' }}
          >
            จ่ายเงิน
          </Button>
        )}
      </Box>

      <CompletePaymentModal open={completeOpen} onClose={() => setCompleteOpen(false)} payment={payment} />

      {/* Expand: Project rows */}
      <Collapse in={open}>
        <Box sx={{ borderTop: '1px solid rgb(var(--color-primary-200))', borderLeft: '3px solid #8b5cf6', ml: 3, backgroundColor: '#faf8ff' }}>

          <Box sx={{ px: 2, py: 0.5, backgroundColor: '#ddd6fe', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 3, height: 12, borderRadius: 1, backgroundColor: '#7c3aed' }} />
            <Typography variant="caption" fontWeight={700} color="#5b21b6" sx={{ letterSpacing: 0.5 }}>
              PROJECT GROUPS
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 1.5 }}>
            {projectKeys.length === 0 ? (
              <Typography variant="body2" color="text.secondary">ไม่มีรายการเบิก</Typography>
            ) : (
              projectKeys.map((projKey) => (
                <ProjectRow key={projKey} expenses={projectGroups[projKey]} payment={payment} />
              ))
            )}
          </Box>
        </Box>
      </Collapse>

      {/* Copy Snackbar */}
      <Snackbar
        open={copySnack.open}
        autoHideDuration={1500}
        onClose={() => setCopySnack({ open: false, text: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ fontSize: '0.78rem' }}>
          {copySnack.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
